// @ts-strict-ignore
import Cropper from 'cropperjs';
import {ParamComponent} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {panel2react} from 'common/util/react';
import {AvatarImageSize} from 'common/constants';

// eslint-disable-next-line import/no-cycle
import {template} from './template';
import css from './index.inline.styl';

// Impose some kind of resolution limit, server side limit is 10mb
const MAX_AVATAR_SIZE = 1080;

interface AvatarUploadState {
  isUploadModalOpen: boolean;
  sourceImage: string | null;
  avatar: string | null;
  isCropperReady: boolean;
  isEditingImage: boolean;
}

interface AvatarUploadParams {
  name: string;
  id: number;
  avatarUrl: string;
  hideName: boolean;
}

//this value must be consistent with index.styl width
const CROPPER_EDITOR_WIDTH = 600;
const INITIAL_CROP_SIZE = 360;

export class AvatarUpload extends ParamComponent<AvatarUploadParams, AvatarUploadState> {
  declare fileInput;
  declare avatarImg;
  declare cropper: Cropper;

  get config() {
    return {
      params: {
        name: String,
        id: Number,
        avatarUrl: String,
        hideName: Boolean,
      },

      css,
      template,

      defaultState: {
        isUploadModalOpen: false,
        sourceImage: null,
        avatar: null,
        isCropperReady: false,
        isEditingImage: false,
      },

      useShadowDom: true,

      helpers: {
        fileInputInserted: (vnode) => {
          this.fileInput = vnode.elm;
        },
        avatarImgElementLoad: (target: HTMLImageElement) =>
          window.requestAnimationFrame(() => {
            this.cropper = new Cropper(target, {
              aspectRatio: 1,
              dragMode: `move`,
              viewMode: 2,
              minCropBoxWidth: AvatarImageSize.XL,
              minCropBoxHeight: AvatarImageSize.XL,
              ready: () => {
                this.cropper.setCropBoxData({
                  width: INITIAL_CROP_SIZE,
                  height: INITIAL_CROP_SIZE,
                  top: 0,
                  left: (CROPPER_EDITOR_WIDTH - INITIAL_CROP_SIZE) / 2,
                });
                this.update({isCropperReady: true});
              },
              cropstart: () => {
                this.update({isEditingImage: true});
              },
              cropend: () => {
                this.update({isEditingImage: false});
              },
            });
          }),
        handleUploadClick: () => {
          this.fileInput.click();
        },
        handleModalChange: ({detail}) => {
          if (detail.state === `closed`) {
            this.cropper.destroy();
            this.fileInput.value = ``;
            this.update({
              sourceImage: null,
              isUploadModalOpen: false,
              isCropperReady: false,
            });
          }
        },
        handleImageCropped: async () => {
          /**
           * width is the width the image in respect to the current viewport
           * naturalWidth is the width of the original image
           * cropBoxWidth is the size of the cropBox in respect to the current viewport
           *
           * We do our own simple scaling here because cropperjs upscales by default for larger resolution images.
           */
          const {width, naturalWidth} = this.cropper.getImageData();
          const {width: cropBoxWidth} = this.cropper.getCropBoxData();

          const size = Math.min((cropBoxWidth / width) * naturalWidth, MAX_AVATAR_SIZE);

          const canvas = this.cropper.getCroppedCanvas({
            maxWidth: size,
            maxHeight: size,
          });

          const blob = await this.toBlob(canvas);
          const avatar = URL.createObjectURL(blob);

          this.update({
            avatar,
            isUploadModalOpen: false,
          });

          this.dispatchEvent(
            new CustomEvent(`avatarChanged`, {
              detail: {avatar},
            }),
          );
        },
        handleFileInputChange: () => {
          const file = this.fileInput.files[0];
          if (file) {
            this.update({
              sourceImage: URL.createObjectURL(file),
              isUploadModalOpen: true,
            });
          }
        },
        getAvatarUrl: () => {
          return this.state.avatar || this.params.avatarUrl || ``;
        },
      },
    };
  }

  toBlob(canvas: HTMLCanvasElement) {
    return new Promise<Blob | null>((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      });
    });
  }
}

export const AvatarUploadEl = defineAndMakeJsxFactory(`mp-avatar-upload`, AvatarUpload);
export const AvatarUploadReact = panel2react(`mp-avatar-upload`, AvatarUpload);
